import { apiFetch, getAuthTokenFromCookie, signInWithToken } from '@/utils'
import { useCallback, useEffect, useState } from 'react'
import { useToast } from './use-toast'
import { auth } from '@/constants'
import { useLocation } from 'wouter'

export const useAuthorizeUserWithToken = (
  uid: string | undefined,
  loading: boolean,
  setUserDataLoading: (isLoading: boolean) => void,
  disconnectWallet: () => Promise<void>
) => {
  const [authToken, setAuthToken] = useState('')
  const [isNewToken, setIsNewToken] = useState(true)
  const [initialMobileAuthLoading, setInitialMobileAuthLoading] = useState(true)

  const { toast } = useToast()
  const [, navigate] = useLocation()

  useEffect(() => {
    // Set an interval to check for cookie changes
    const interval = setInterval(() => {
      if (loading || (isNewToken && authToken)) {
        return
      }

      const newToken = getAuthTokenFromCookie()

      if (!newToken) setInitialMobileAuthLoading(false)
      if (newToken && newToken !== authToken) {
        setAuthToken(newToken)
        setIsNewToken(true)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [loading, isNewToken, authToken])

  const signInUserWithToken = useCallback(
    async (passedAuthToken?: string) => {
      setUserDataLoading(true)
      // passed token used in case when token passed via postMessage method
      // auth token for the case with cookies
      const computedAuthToken = passedAuthToken || authToken
      try {
        const response = await apiFetch('/v1/verifyToken', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${computedAuthToken}`
          }
        })
        const result = await response.json()
        if (!response.ok) {
          setUserDataLoading(false)
          setIsNewToken(false)
          setInitialMobileAuthLoading(false)
          const errorData = result.error
          throw new Error(
            `${errorData?.title ? errorData?.title + ' .' : ''}${
              errorData?.details ||
              errorData?.message ||
              'Something went wrong during auth verification'
            }`
          )
        }

        if (uid && uid === result?.uid) {
          setIsNewToken(false)
          setUserDataLoading(false)
          setInitialMobileAuthLoading(false)
          return
        }
        // If user is already logged in but the token is different -> sign out first
        if (uid !== result?.uid) {
          await disconnectWallet()
          await auth.signOut()
        }

        await signInWithToken(result?.customToken)
        setIsNewToken(false)
        setInitialMobileAuthLoading(false)
        toast({ title: 'Success', description: 'User signed in successfully' })
      } catch (error) {
        setUserDataLoading(false)
        setInitialMobileAuthLoading(false)
        console.error('Can`t sign in user: ', error)
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to authorize mobile user'

        toast({
          title: 'Sign in failed',
          description: errorMessage,
          variant: 'destructive'
        })
      }
    },
    [authToken, loading, uid, isNewToken]
  )

  useEffect(() => {
    isNewToken && authToken && !loading && signInUserWithToken()
  }, [authToken, loading, isNewToken])

  // useEffect(() => {
  //   const handleMessage = async (event: MessageEvent) => {
  //     EventTarget
  //     if (!event.data) return
  //     try {
  //       let message

  //       if (typeof event.data === 'string') {
  //         message = JSON.parse(event.data)
  //       } else if (typeof event.data === 'object') {
  //         message = event.data // Already an object
  //       } else {
  //         console.warn('Invalid message format:', event.data)
  //         return
  //       }

  //       if (!message?.actionType) return

  //       if (message.actionType === 'logout') {
  //         await auth.signOut()
  //       }

  //       if (message.actionType === 'signIn') {
  //         if (!message?.token) {
  //           toast({
  //             title: "Can't sign in user",
  //             description: 'Token is missing, but required.',
  //             variant: 'destructive'
  //           })
  //         }

  //         await signInUserWithToken(message?.token)
  //       }
  //     } catch (error) {
  //       console.error('Invalid message received:', error)
  //     }
  //   }

  //   window.addEventListener('message', handleMessage)
  //   return () => window.removeEventListener('message', handleMessage)
  // }, [])

  return initialMobileAuthLoading
}
